import type { ProductSpec, ProductLine, ProductVariant } from '@/types/product';

/**
 * Bewerkingen op een ProductSpec. Losgetrokken van de component zodat het
 * gedrag — vooral het hernoemen van varianten en regelcodes — te testen is
 * zonder React.
 */

/** Woorden die niets toevoegen aan een code en hem alleen onleesbaar maken. */
const FILLER_WORDS = new Set([
  'van',
  'de',
  'het',
  'een',
  'en',
  'per',
  'incl',
  'excl',
  'voor',
  'met',
  'uren',
  'uur',
]);

// Ruim genoeg voor twee betekenisvolle woorden (projectmanagement_planning),
// kort genoeg om in een formule leesbaar te blijven.
const MAX_CODE_LENGTH = 28;

export const slugifyCode = (value: string): string => {
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

  // Codes worden in formules overgetypt, dus kort en leesbaar wint het van
  // volledig. 'SEO (10% van design + dev uren)' hoeft geen
  // seo_10_van_design_dev_uren te worden.
  const words = normalized.split('_').filter(Boolean);
  const meaningful = words.filter((word) => !FILLER_WORDS.has(word));
  const parts = meaningful.length ? meaningful : words;

  let base = '';
  for (const word of parts) {
    const candidate = base ? `${base}_${word}` : word;
    if (candidate.length > MAX_CODE_LENGTH && base) break;
    base = candidate;
    if (base.length >= MAX_CODE_LENGTH) break;
  }

  base = base.slice(0, MAX_CODE_LENGTH).replace(/_+$/, '') || 'regel';
  return /^[0-9]/.test(base) ? `r_${base}` : base;
};

export const uniqueCode = (desired: string, taken: string[]): string => {
  const base = slugifyCode(desired);
  if (!taken.includes(base)) return base;
  let suffix = 2;
  while (taken.includes(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

export const addVariant = (spec: ProductSpec, name: string): ProductSpec => {
  const id = uniqueCode(
    name,
    spec.variants.map((variant) => variant.id)
  );
  const variant: ProductVariant = { id, name };
  return {
    ...spec,
    variants: [...spec.variants, variant],
    // Een nieuwe variant begint met de uren van de eerste variant, zodat je
    // alleen hoeft aan te passen wat afwijkt.
    lines: spec.lines.map((line) => ({
      ...line,
      hours: { ...line.hours, [id]: line.hours[spec.variants[0]?.id] ?? 0 },
    })),
    fixedPriceCents: spec.fixedPriceCents
      ? { ...spec.fixedPriceCents, [id]: spec.fixedPriceCents[spec.variants[0]?.id] ?? 0 }
      : spec.fixedPriceCents,
  };
};

export const removeVariant = (spec: ProductSpec, variantId: string): ProductSpec => {
  if (spec.variants.length <= 1) return spec;
  const strip = <T>(record: Record<string, T> | undefined) => {
    if (!record) return record;
    const next = { ...record };
    delete next[variantId];
    return next;
  };
  return {
    ...spec,
    variants: spec.variants.filter((variant) => variant.id !== variantId),
    lines: spec.lines.map((line) => ({ ...line, hours: strip(line.hours) ?? {} })),
    fixedPriceCents: strip(spec.fixedPriceCents),
    cost: { ...spec.cost, amountCentsByVariant: strip(spec.cost.amountCentsByVariant) },
  };
};

export const duplicateLastVariant = (spec: ProductSpec): ProductSpec => {
  const last = spec.variants[spec.variants.length - 1];
  if (!last) return addVariant(spec, 'Standaard');
  const base = last.name.replace(/ \(kopie(?: \d+)?\)$/, '') || 'Variant';
  const names = new Set(spec.variants.map((variant) => variant.name));
  let name = `${base} (kopie)`;
  for (let index = 2; names.has(name); index++) name = `${base} (kopie ${index})`;
  const id = uniqueCode(
    name,
    spec.variants.map((variant) => variant.id)
  );
  const copyValue = <T>(values: Record<string, T> | undefined) =>
    values && last.id in values ? { ...values, [id]: values[last.id] } : values;
  return {
    ...spec,
    variants: [...spec.variants, { ...last, id, name }],
    lines: spec.lines.map((line) => ({
      ...line,
      hours: { ...line.hours, [id]: line.hours[last.id] ?? 0 },
    })),
    fixedPriceCents: copyValue(spec.fixedPriceCents),
    cost: { ...spec.cost, amountCentsByVariant: copyValue(spec.cost.amountCentsByVariant) },
  };
};

export const addLine = (spec: ProductSpec, rateKey: string): ProductSpec => {
  const code = uniqueCode(
    'nieuwe_regel',
    spec.lines.map((line) => line.code)
  );
  const line: ProductLine = {
    id: code,
    code,
    description: '',
    rateKey,
    hours: Object.fromEntries(spec.variants.map((variant) => [variant.id, 0])),
  };
  return { ...spec, lines: [...spec.lines, line] };
};

export const removeLine = (spec: ProductSpec, lineId: string): ProductSpec => ({
  ...spec,
  lines: spec.lines.filter((line) => line.id !== lineId),
});

/** Verplaatst één regel zonder de inhoud te wijzigen. */
export const moveLine = (spec: ProductSpec, lineId: string, targetIndex: number): ProductSpec => {
  const currentIndex = spec.lines.findIndex((line) => line.id === lineId);
  if (currentIndex < 0 || spec.lines.length < 2) return spec;

  const boundedTarget = Math.max(0, Math.min(targetIndex, spec.lines.length - 1));
  if (boundedTarget === currentIndex) return spec;

  const lines = [...spec.lines];
  const [line] = lines.splice(currentIndex, 1);
  lines.splice(boundedTarget, 0, line);
  return { ...spec, lines };
};

export const updateLine = (
  spec: ProductSpec,
  lineId: string,
  patch: Partial<ProductLine>
): ProductSpec => ({
  ...spec,
  lines: spec.lines.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
});

export const setHours = (
  spec: ProductSpec,
  lineId: string,
  variantId: string,
  raw: string
): ProductSpec => {
  const trimmed = raw.trim();
  let value: number | string;
  if (trimmed.startsWith('=')) {
    value = trimmed;
  } else if (trimmed === '') {
    value = 0;
  } else {
    const parsed = Number(trimmed.replace(',', '.'));
    value = Number.isFinite(parsed) ? parsed : trimmed;
  }

  return {
    ...spec,
    lines: spec.lines.map((line) =>
      line.id === lineId ? { ...line, hours: { ...line.hours, [variantId]: value } } : line
    ),
  };
};

export const hoursToInput = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '';
  return typeof value === 'number' ? String(value).replace('.', ',') : value;
};

/** Regels waarvan een formule naar `code` verwijst. Gebruikt bij hernoemen en verwijderen. */
export const lineReferences = (spec: ProductSpec, code: string): string[] => {
  if (!code) return [];
  const pattern = new RegExp(`\\b${code}\\b`);
  return spec.lines
    .filter(
      (line) =>
        line.code !== code &&
        Object.values(line.hours).some(
          (value) => typeof value === 'string' && value.startsWith('=') && pattern.test(value)
        )
    )
    .map((line) => line.code);
};
